import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { KeyRound, Loader2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { STEP_KEY, getVerified, setVerified, useStaff, verifiedUntil } from "@/lib/staff";
import { BTN, BTN_GHOST, DIALOG, INPUT } from "@/components/staff/ui";

const googleUrl = () => `https://auth.emergentagent.com/?redirect=${encodeURIComponent(`${window.location.origin}/staff/verify`)}`;

function CodeStep({ email, onNext }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await http.post("/staff/verify/code"); setSent(true); toast.success(`Code sent to ${email}`); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const check = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await http.post("/staff/verify/code/check", { code: code.trim() }); onNext(data); } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  if (!sent) return <button type="button" onClick={send} disabled={busy} className={BTN} data-testid="staff-verify-send-code">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}Email me a code</button>;
  return (
    <form onSubmit={check} className="space-y-3">
      <input autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} inputMode="numeric" autoComplete="one-time-code" maxLength={6}
        placeholder="6-digit code" className={`${INPUT} w-full`} aria-label="Emailed code" data-testid="staff-verify-code-input" />
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={send} disabled={busy} className="text-xs text-sky-300 hover:underline" data-testid="staff-verify-resend">Send a new code</button>
        <button type="submit" disabled={busy || code.length !== 6} className={BTN} data-testid="staff-verify-code-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Continue</button>
      </div>
    </form>
  );
}

function SignInStep({ step, onDone }) {
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const google = () => {
    sessionStorage.setItem(STEP_KEY, JSON.stringify({ token: step.step_token, back: location.pathname + location.search }));
    window.location.href = googleUrl();
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/staff/verify/finish", { step_token: step.step_token, password });
      setVerified(data.verified_token, data.expires_in);
      onDone(data.verified_token);
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  if (step.second_step === "google") return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-400">Now sign in with Google again, using your staff account. You'll come straight back here; then make your change again.</p>
      <button type="button" onClick={google} className={BTN} data-testid="staff-verify-google-btn">Sign in with Google again</button>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-neutral-400">Now sign in again with your password.</p>
      <input autoFocus type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password"
        className={`${INPUT} w-full`} aria-label="Password" data-testid="staff-verify-password-input" />
      <div className="flex justify-end"><button type="submit" disabled={busy || !password} className={BTN} data-testid="staff-verify-password-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Confirm</button></div>
    </form>
  );
}

function VerifyDialog({ me, onDone, onCancel }) {
  const [step, setStep] = useState(null);
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className={DIALOG} data-testid="staff-verify-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-sky-300" />Confirm it's you</DialogTitle>
          <DialogDescription className="text-neutral-400">
            {step ? "Step 2 of 2 · Sign in again" : `Step 1 of 2 · We'll email a 6-digit code to ${me.email}.`} Once confirmed, sensitive changes are allowed on this device for 10 minutes.
          </DialogDescription>
        </DialogHeader>
        {step ? <SignInStep step={step} onDone={onDone} /> : <CodeStep email={me.email} onNext={setStep} />}
      </DialogContent>
    </Dialog>
  );
}

// Role changes and deletions need an emailed code plus a fresh sign-in (Google, or password for accounts without Google).
export function useStaffVerify(me) {
  const [req, setReq] = useState(null);
  const getToken = useCallback(() => {
    const t = getVerified();
    if (t) return Promise.resolve(t);
    return new Promise((resolve, reject) => setReq({ resolve, reject }));
  }, []);
  const dialog = req && (
    <VerifyDialog me={me} onDone={(t) => { req.resolve(t); setReq(null); }} onCancel={() => { req.reject({ cancelled: true }); setReq(null); }} />
  );
  return { getToken, dialog };
}

export function VerifyStatus() {
  const { verify } = useStaff();
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);
  const left = Math.ceil((verifiedUntil() - Date.now()) / 60000);
  if (left > 0) return <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300" data-testid="staff-verified-status"><ShieldCheck className="h-3.5 w-3.5" />Identity confirmed · {left} min left</span>;
  return (
    <button onClick={() => verify.getToken().then(() => tick((n) => n + 1)).catch(() => {})} className={BTN_GHOST} data-testid="staff-verify-start-btn">
      <KeyRound className="h-4 w-4" />Confirm it's you
    </button>
  );
}
